'use client'

import { ArrowRightLeft, Plus, X } from 'lucide-react'
import { GrassTuft } from '@/components/icons/grass-tuft'
import { cn } from '@/lib/utils'
import { useStuckPicker } from './use-stuck-picker'
import { getGrassComparisonUi } from '@/data/grass-comparison-ui'

interface PickerBarProps {
  /** The currently picked grass names, in order. */
  selectedGrasses: string[]
  /** Display name of the winning grass shown in the "stuck" chip. Null when
   *  tied or no valid comparison yet. */
  championName: string | null
  onSwap?: () => void
  onRemove?: (index: number) => void
  onAddThird?: () => void
  /** Pickers rendered as children; one per selected grass. The parent owns the
   *  actual GrassSelector dropdowns to keep keyboard nav / a11y in one place. */
  children: React.ReactNode
}

export function PickerBar({
  selectedGrasses,
  championName,
  onSwap,
  onRemove,
  onAddThird,
  children,
}: PickerBarProps) {
  const { ref, stuck } = useStuckPicker<HTMLDivElement>()
  const canAddThird = selectedGrasses.length < 3 && !!onAddThird
  const showSwap = selectedGrasses.length === 2 && !!onSwap
  const showRemove = selectedGrasses.length > 2 && !!onRemove

  const championUi = championName ? getGrassComparisonUi(championName) : null
  const showChampionChip = stuck && championName !== null

  return (
    <div
      ref={ref}
      className={cn(
        'min-[760px]:sticky top-[70px] z-30 border-y border-gray-200 bg-gray-50/95 px-4 backdrop-blur-sm transition-shadow sm:px-6 lg:top-[78px] lg:px-8',
        stuck && 'shadow-elevation-2',
      )}
    >
      <div className="flex flex-col gap-2.5 py-3 sm:gap-3 sm:py-4 min-[760px]:flex-row min-[760px]:items-center">
        {showChampionChip && championName && (
          <span className="inline-flex max-w-full items-center gap-2 self-start rounded-full border border-gray-200 bg-white py-1.5 pl-1.5 pr-3 text-xs font-bold text-gray-900 shadow-sm">
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: championUi?.accentColor ?? '#16a34a' }}
              aria-hidden="true"
            >
              <GrassTuft className="h-4 w-4" />
            </span>
            <span className="truncate">{championName} wins</span>
          </span>
        )}

        <div className="grid w-full min-w-0 flex-1 grid-cols-1 gap-2 min-[560px]:grid-cols-2 min-[760px]:flex min-[760px]:flex-wrap min-[760px]:items-center min-[760px]:gap-3">
          {children}
        </div>

        <div className="flex w-full items-center gap-2 min-[560px]:w-auto min-[760px]:ml-auto">
          {showSwap && (
            <button
              type="button"
              onClick={onSwap}
          aria-label="Swap Grass 1 and Grass 2"
          title="Swap Grass 1 and Grass 2"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:border-green-400 hover:text-green-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 min-[760px]:h-9 min-[760px]:w-9"
            >
              <ArrowRightLeft className="h-4 w-4" />
            </button>
          )}
          {showRemove && (
            <button
              type="button"
              onClick={() => onRemove?.(selectedGrasses.length - 1)}
              aria-label="Remove last grass"
              title="Remove last grass"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:border-red-400 hover:text-red-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 min-[760px]:h-9 min-[760px]:w-9"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {canAddThird && (
            <button
              type="button"
              onClick={onAddThird}
              className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-dashed border-green-500 bg-white px-3.5 text-xs font-semibold text-green-700 transition-colors hover:bg-green-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 min-[560px]:flex-none min-[760px]:h-9"
            >
              <Plus className="h-3.5 w-3.5" />
              Add a third grass
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
